// Notificatore diagnostico process-local. Inoltra ogni tappa del ciclo di vita
// di un evento (enqueue/send/drop/timeout/shutdown) all'hook `onDiagnostic`, se
// configurato. Specchio di `CloseYourIt.notify_diagnostic` (gem Ruby, CYRB-12).
//
// Non ricorsivo per costruzione: mentre un hook è in esecuzione la soppressione è
// attiva — il Client vi gata sopra le API di cattura (niente telemetria dalla
// diagnostica) e un ulteriore `notify` viene ignorato (niente loop). Un errore
// sollevato dall'hook è assorbito: la diagnostica non deve mai far crashare
// l'host né interrompere l'invio in corso.
//
// La soppressione è PROCESSO-globale (come il thread-local della gem Ruby), non
// per-client: durante un `init` a caldo (CYJS-8) il vecchio client può essere in
// chiusura mentre il nuovo è già in uso, e l'hook del vecchio potrebbe catturare
// via la facade sul nuovo client. Un flag globale garantisce che «mentre un
// qualsiasi hook gira, nessuna cattura passa» valga per l'intero processo.

import type { DiagnosticDetails, DiagnosticEvent, OnDiagnostic } from './types'

// True mentre un hook diagnostico è in esecuzione, ovunque nel processo. JS è
// single-thread e `notify` è sincrona: un solo hook gira alla volta → basta un
// boolean, sempre riportato a `false` nel `finally`.
let active = false

/// `true` se un QUALSIASI hook diagnostico è in esecuzione nel processo. Le API
/// di cattura vi gatano sopra per non generare telemetria dalla diagnostica,
/// anche attraverso la facade dopo una re-init (CYJS-9).
export function diagnosticsSuppressed(): boolean {
  return active
}

/// Solo il NOME della classe d'errore, mai il messaggio: quest'ultimo potrebbe
/// contenere URL o dati sensibili e la diagnostica non deve mai esporre
/// payload/segreti. Fallback `'Error'` per valori non-Error.
export function errorName(error: unknown): string {
  return error instanceof Error ? error.name : 'Error'
}

export class Diagnostics {
  private readonly hook: OnDiagnostic | undefined

  constructor(hook?: OnDiagnostic) {
    this.hook = hook
  }

  /// `true` mentre un hook diagnostico è in esecuzione (soppressione globale).
  get suppressed(): boolean {
    return active
  }

  /// Notifica [event] all'hook con [details]. No-op senza hook o durante un
  /// notify già in corso (di questa o di un'altra istanza). Un'eccezione
  /// dell'hook è assorbita.
  notify(event: DiagnosticEvent, details: DiagnosticDetails = {}): void {
    if (!this.hook || active) return
    active = true
    try {
      this.hook(event, details)
    } catch {
      // Hook difettoso: la diagnostica non deve mai propagare né interrompere.
    } finally {
      active = false
    }
  }
}
